/** aqpro (en) */
const data = {
    "views": {
        "intro": "Atmos Air Quality shows current air quality and forecasts as a Garmin widget. Swipe up or down, or press UP / DOWN, to move between the pages you enabled in settings.",
        "current": {
            "title": "Current air quality",
            "gauge": "Large coloured gauge: current AQI (EU or US, depending on your setting). The marker shows where you are on the scale.",
            "category": "Category label (e.g. Good / Moderate / Poor) under or next to the value.",
            "pollutants": "PM2.5, PM10 and O₃ values are shown as quick reference.",
            "location": "Location name and how long ago the data was updated."
        },
        "hourly": {
            "title": "24-hour forecast",
            "bars": "Bar chart of AQI for the next 24 hours, starting from the current hour.",
            "colors": "Bar colours follow the same AQI categories as the main gauge.",
            "axis": "Time runs left → right (now → later)."
        },
        "daily": {
            "title": "5-day forecast",
            "range": "Each day shows the expected AQI maximum and minimum.",
            "use": "Useful for planning outdoor activities over the coming days."
        },
        "dust": {
            "title": "Saharan dust / dust",
            "current": "Current dust level plus a 24-hour trend.",
            "daily": "Multi-day dust outlook when data is available.",
            "note": "Especially relevant during Saharan dust events that raise PM levels."
        },
        "particles": {
            "title": "Particle comparison",
            "dual": "Dual chart comparing two series you choose in settings (e.g. PM2.5 vs PM10, or O₃ vs NO₂).",
            "single": "If only one series is selected, it uses the full width.",
            "series": "Available series: PM2.5, PM10, Dust, UV, O₃, NO₂, CO, SO₂, AOD."
        },
        "visibility": {
            "title": "Haze / visibility (AOD)",
            "aod": "Based on Aerosol Optical Depth (AOD) – how much aerosols dim the atmosphere.",
            "km": "Converted into an approximate visibility in kilometres or miles, depending on the units of your watch.",
            "chart": "24-hour chart: taller bars ≈ better visibility; colour indicates haze density."
        },
        "pollen": {
            "title": "Pollen (Europe)",
            "species": "Six types: alder, birch, grass, mugwort, olive, ragweed – each with a level bar.",
            "chart": "Bottom chart: 24-hour maximum across all types.",
            "europe": "Data comes from CAMS and is typically available in Europe only. Outside Europe you may see “No data”."
        },
        "glance": {
            "title": "Glance view",
            "what": "Widget glance: current AQI value, category and a colour scale with marker.",
            "mono": "On monochrome Instinct watches a progress bar is used instead of the colour scale."
        },
        "allValues": {
            "title": "All values",
            "list": "All measured values on one page: PM10, PM2.5, CO, NO₂, SO₂ and O₃, plus CO₂ (ppm) and methane (CH₄)."
        }
    },
    "controls": {
        "nav": {
            "title": "Navigate between pages",
            "swipe": "Touchscreen: swipe up or down to change pages. A tap also opens the next page.",
            "buttons": "Button watches: UP / DOWN, or START to go to the next page. Edge devices: tap the screen for the next page.",
            "order": "Only pages you assigned in settings (Page 1–8) appear. Slots set to “None” are skipped."
        },
        "refresh": {
            "title": "Update now",
            "text": "Open the menu (hold MENU, or hold the screen on touch watches) and choose “Force update”. The watch needs a Bluetooth connection to the phone for the download.",
            "bg": "In the background the app also updates automatically using the interval from settings (default 60 minutes)."
        },
        "menu": {
            "title": "On-watch menu",
            "text": "Hold MENU, or hold the screen on touch watches. The menu lets you switch between European AQI and US AQI, choose “Force update”, start a “GPS fix” and open “Troubleshooting”. The AQI standard is the same setting as in Connect IQ Mobile; whichever you change last wins."
        },
        "location": {
            "title": "Location",
            "text": "By default the app uses the location of the watch or phone (setting “Location source”). To use a fixed place, turn off “Automatic location” and enter latitude and longitude in the settings.",
            "gps": "If the location is missing or outdated, choose “GPS fix” in the menu (outdoors), then “Force update”."
        },
        "connection": {
            "title": "Phone connection",
            "text": "Internet requests go through the paired phone. If you see a connection error, open Garmin Connect, keep Bluetooth on, and try a manual refresh."
        }
    },
    "index": {
        "intro": {
            "title": "Two standards – one app",
            "text": "Atmos Air Quality can show either the European Air Quality Index (EAQI) or the US AQI (EPA). The number, colour scale and category labels change with the standard – the underlying pollutant measurements stay the same.",
            "switch": "Switch in Connect IQ settings (“AQI standard”) or via the on-watch menu."
        },
        "diff": {
            "title": "Main differences",
            "scale": "Scale: EU typically runs with lower absolute numbers (gauge ends around 120). US AQI commonly goes up to 300+ on the gauge.",
            "thresholds": "Category thresholds differ – the same air can look “moderate” in one system and “unhealthy for sensitive groups” in the other.",
            "use": "Use EU if you are used to European apps/media; use US if you compare with US EPA / AirNow-style reports."
        },
        "table": {
            "range": "Index",
            "category": "Category"
        },
        "eu": {
            "title": "European AQI (EAQI)",
            "text": "Combines several pollutants into one index. Categories roughly: Good → Fair → Moderate → Poor → Very Poor → Extremely Poor.",
            "c0": "Good",
            "c1": "Fair",
            "c2": "Moderate",
            "c3": "Poor",
            "c4": "Very Poor",
            "c5": "Extremely Poor"
        },
        "us": {
            "title": "US AQI (EPA)",
            "text": "Widely used in the US. Categories: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.",
            "c0": "Good",
            "c1": "Moderate",
            "c2": "Unhealthy for Sensitive Groups",
            "c3": "Unhealthy",
            "c4": "Very Unhealthy",
            "c5": "Hazardous"
        },
        "warning": "Do not mix the two numbers. An “80” on the EU scale is not the same as an “80” on the US scale."
    },
    "values": {
        "intro": {
            "title": "What do the values mean?",
            "text": "The AQI is a summary index. The pollutant pages and particle chart let you see which component is elevated. Units for particles and gases are typically µg/m³ unless noted otherwise."
        },
        "pm25": {
            "title": "PM2.5",
            "text": "Fine particles smaller than 2.5 µm. They can penetrate deep into the lungs. Often the main driver of a bad AQI in cities and during wood smoke / wildfire events."
        },
        "pm10": {
            "title": "PM10",
            "text": "Coarser particles up to 10 µm (dust, pollen fragments, road abrasion). Less deep into the lungs than PM2.5, but still relevant for respiratory irritation."
        },
        "o3": {
            "title": "Ozone (O₃)",
            "text": "Ground-level ozone forms from traffic/industry gases in sunlight. Often higher on sunny afternoons; can irritate airways during outdoor sports."
        },
        "no2": {
            "title": "Nitrogen dioxide (NO₂)",
            "text": "Typically linked to traffic and combustion. Elevated near busy roads; part of the European index calculation."
        },
        "so2": {
            "title": "Sulphur dioxide (SO₂)",
            "text": "From burning sulphur-containing fuels and some industrial sources. Usually lower in many Western European cities than PM or NO₂."
        },
        "co": {
            "title": "Carbon monoxide (CO)",
            "text": "Incomplete combustion (traffic, heating). Dangerous in high indoor concentrations; outdoor levels are usually much lower."
        },
        "dust": {
            "title": "Dust",
            "text": "Mineral / Saharan dust load. Spikes can raise PM values across large regions even far from the source."
        },
        "uv": {
            "title": "UV index",
            "text": "Sunburn risk index (not an air pollutant). Handy next to outdoor air-quality planning on clear days."
        },
        "aod": {
            "title": "AOD / visibility",
            "text": "Aerosol Optical Depth describes how “hazy” the atmosphere is. The app maps it to an approximate visibility in km (clear → dense haze)."
        },
        "pollen": {
            "title": "Pollen",
            "text": "Allergy-relevant pollen counts (Europe). Levels are shown per species; the chart shows the hourly maximum across all types."
        },
        "disclaimer": "This page is educational, not medical advice. For health decisions follow local official air-quality guidance."
    },
    "settings": {
        "intro": {
            "title": "Settings overview",
            "text": "Open the app settings in Garmin Connect IQ (phone) or Garmin Express. The AQI standard can also be changed from the on-watch menu."
        },
        "aqStd": {
            "title": "AQI standard",
            "text": "European or US. Affects the number, colours and category labels everywhere (main view, charts, glance, complications)."
        },
        "pages": {
            "title": "Pages 1–8",
            "text": "Each slot can be one of: Current, 24h forecast, 5-day forecast, Dust, Particles, Haze/visibility, Pollen, All values, or None to hide the slot.",
            "tip": "Tip: put Current first, then the forecasts you use most. Hide unused pages to swipe less."
        },
        "series": {
            "title": "Particle chart: series A / B",
            "text": "Choose which two metrics the particle comparison chart shows (PM2.5, PM10, Dust, UV, O₃, NO₂, CO, SO₂, AOD). Set a series to “None” to hide it; one series uses the full chart width."
        },
        "location": {
            "title": "Automatic location and coordinates",
            "text": "“Automatic location” is on by default and uses the source chosen in “Location source” (newest position, phone or watch). Turn it off to enter a fixed latitude and longitude."
        },
        "interval": {
            "title": "Update interval in minutes",
            "text": "How often the background service should refresh (minutes). Minimum 10, maximum 720. Default 60. Shorter intervals use more phone data and battery."
        },
        "comp": {
            "title": "Complications",
            "forDevs": "For developers:",
            "intro": "This app publishes complications for use in watch faces",
            "aqi": "\"AtmosAirAQI\": Current air quality index (EU or US index, depending on the user’s selection)",
            "aqiRange": "Includes the “Range” indicating where this value falls.",
            "ts": "\"AtmosAirTs\": UTC timestamp of the current value",
            "longLabel": "Long Label: “AtmosAirAQI”"
        },
        "limits": {
            "title": "Device limits (low memory)",
            "warning": "On some older / low-memory models (e.g. Instinct 2, Forerunner 55, Fenix 6 and similar) the extra pages Dust, Particles, Haze and Pollen are not available. Current + forecast pages remain.",
            "text": "This is intentional so the widget stays stable within Garmin’s memory limits on those devices."
        },
        "supportBefore": "Questions or issues? Please use the",
        "supportLink": "contact form",
        "supportAfter": "."
    }
};
export default data;
